import { QueryClient } from '@tanstack/react-query';

/**
 * Configured QueryClient for Filmpl
 *
 * Phase 5E - Spotify-model caching with persistence:
 * - staleTime: 60s default (lists), media details use 5 min in hooks
 * - gcTime: 30 minutes max (bounded for persistence)
 * - refetchOnMount: false (Spotify UX - instant from cache on navigation)
 * - refetchOnWindowFocus: true (SWR - refresh stale data in background)
 * - refetchOnReconnect: true (recover from offline)
 * - networkMode: 'offlineFirst' (use cache while fetching)
 * - retry: exponential backoff for network errors
 *
 * Key behaviors:
 * - Cache persists to IndexedDB per-user
 * - Stale data shown instantly on navigation (no flicker)
 * - Revalidation happens on focus/reconnect (not every mount)
 * - Mutations trigger targeted invalidation for correctness
 * - Bounded cache size (500 entries, 10MB)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 60 seconds (lists)
      // Individual hooks can override for different domains
      staleTime: 60 * 1000,

      // Keep unused data in cache for 30 minutes (bounded for persistence)
      gcTime: 30 * 60 * 1000,

      // SWR: refetch stale data when window regains focus
      refetchOnWindowFocus: true,

      // Spotify UX: don't refetch on every mount (use cached data)
      // Correctness maintained via:
      // - refetchOnWindowFocus (background revalidation)
      // - refetchOnReconnect (network recovery)
      // - Mutation onSuccess invalidation (targeted freshness)
      refetchOnMount: false,

      // Refetch when network reconnects (recover from offline)
      refetchOnReconnect: true,

      // Use cache while fetching (offlineFirst)
      // Shows cached data instantly, fetches in background
      networkMode: 'offlineFirst',

      // Retry failed requests with exponential backoff
      retry: (failureCount, error) => {
        // Never retry AbortError - these are intentional cancellations (spam-click supersede)
        if (error instanceof Error && error.name === 'AbortError') {
          return false;
        }
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && 'status' in error) {
          const status = (error as Error & { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once on network error
      retry: 1,
      retryDelay: 1000,
      // Mutations require network
      networkMode: 'online',
    },
  },
});

// DEV tooling: allow DevTools to subscribe to query cache events
// Usage in browser console:
//   window.__RQ_LOG_LISTS__()   // Start logging list cache writes
//   window.__RQ_UNSUB__()       // Stop logging
//   window.__RQ_QUERY_CLIENT__  // Access QueryClient directly
if (import.meta.env.DEV && typeof window !== 'undefined') {
  import('./reactQueryDebug').then(({ attachReactQueryDebug }) => {
    attachReactQueryDebug(queryClient);
  });
}
